import { Component } from "@theme/component";
import { API_ENDPOINTS } from "@theme/api-endpoints";
import { getApiLocale } from "@theme/locale-map";
import { trackFormSubmit } from "@theme/analytics";

/**
 * URL param names the Join site reads (MIS-185601). Change a name here only
 * together with the Join site.
 */
const JOIN_PARAMS = {
  portal: "portal_id",
  country: "country",
  language: "langCode",
};

/**
 * Reads the visitor's portal id from the global PortalManager, if it has loaded:
 * the signed-in customer's portal first, then the portal_id cookie. Returns null
 * when PortalManager isn't available or has no valid value.
 * @returns {string|null}
 */
function getPortalId() {
  return window.PortalManager?.getPortalId?.() || null;
}

/**
 * Returns `href` with the Join params (portal_id, country, langCode) appended,
 * preserving all existing params, hash and host. Country and langCode are the
 * storefront's 3-letter codes from getApiLocale. A param already on the URL is
 * left as-is, and a param with no value is omitted. Returns `href` unchanged
 * when there's nothing to add, the href is empty, or the URL can't be parsed.
 * @param {string} href
 * @returns {string}
 */
function withJoinParams(href) {
  if (!href) return href;
  const { countryCode, languageCode } = getApiLocale();
  const params = [
    [JOIN_PARAMS.portal, getPortalId()],
    [JOIN_PARAMS.country, countryCode],
    [JOIN_PARAMS.language, languageCode],
  ];

  try {
    const url = new URL(href, window.location.origin);
    let changed = false;
    params.forEach(([name, value]) => {
      if (!value || url.searchParams.has(name)) return;
      url.searchParams.set(name, value);
      changed = true;
    });
    return changed ? url.toString() : href;
  } catch {
    return href;
  }
}

/**
 * @typedef {object} JoinFormFieldRefs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} error
 *
 * @extends {Component<JoinFormFieldRefs>}
 */
class JoinFormField extends Component {
  requiredRefs = ["input", "error"];

  connectedCallback() {
    super.connectedCallback();
    this.refs.input.addEventListener("invalid", this.handleInvalid);
    this.refs.input.addEventListener("input", this.handleInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.input.removeEventListener("invalid", this.handleInvalid);
    this.refs.input.removeEventListener("input", this.handleInput);
  }

  handleInvalid = (event) => {
    event.preventDefault();
    this.toggleAttribute("invalid", true);
    const input = this.refs.input;
    if (input.validity.valueMissing) {
      this.refs.error.textContent = input.dataset.requiredMessage || input.validationMessage;
    } else if (input.validity.typeMismatch) {
      this.refs.error.textContent = input.dataset.emailInvalidMessage || input.validationMessage;
    } else {
      this.refs.error.textContent = input.validationMessage;
    }
  };

  handleInput = () => {
    if (!this.refs.input.validity.valid) return;
    this.toggleAttribute("invalid", false);
    this.refs.error.textContent = "";
  };
}

/**
 * @typedef {object} PopupModalRefs
 * @property {HTMLDialogElement} dialog
 *
 * @extends {Component<PopupModalRefs>}
 */
class PopupModal extends Component {
  requiredRefs = ["dialog"];

  connectedCallback() {
    super.connectedCallback();
    this.popupId = this.dataset.popupId;
    this.addEventListener("click", this.handleInternalClick);
    this.addEventListener("submit", this.handleSubmit);
    this.refs.dialog.addEventListener("close", this.handleDialogClose);
    document.addEventListener("click", this.handleTriggerClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.handleInternalClick);
    this.removeEventListener("submit", this.handleSubmit);
    this.refs.dialog.removeEventListener("close", this.handleDialogClose);
    document.removeEventListener("click", this.handleTriggerClick);
    this.unlockPageScroll();
  }

  handleTriggerClick = event => {
    const trigger = event.target.closest("[data-popup-trigger]");
    if (!trigger || trigger.dataset.popupTrigger !== this.popupId) return;
    event.preventDefault();
    this.showPanel("choice");
    this.applyJoinParamsToLinks();
    this.lockPageScroll();
    this.refs.dialog.showModal();
  };

  /**
   * Adds the Join params to the choice panel's link buttons (Start Now). Rebuilt from
   * the original href on every open, so params never stack and stay current.
   */
  applyJoinParamsToLinks() {
    this.querySelectorAll('[data-popup-panel="choice"] a.button[href]').forEach(anchor => {
      if (!anchor.dataset.baseHref) anchor.dataset.baseHref = anchor.getAttribute("href");
      anchor.href = withJoinParams(anchor.dataset.baseHref);
    });
  }

  handleDialogClose = () => {
    this.unlockPageScroll();
  };

  lockPageScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.classList.add("popup-modal-open");
    document.body.classList.add("popup-modal-open");
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  unlockPageScroll() {
    document.documentElement.classList.remove("popup-modal-open");
    document.body.classList.remove("popup-modal-open");
    document.body.style.paddingRight = "";
  }

  handleInternalClick = event => {
    const action = event.target.closest("[data-popup-action]")?.dataset.popupAction;
    if (action === "close") this.refs.dialog.close();
    if (action === "choice") this.showPanel("choice");
    if (action === "discuss") this.showPanel("discuss");
    if (action === "start") this.showPanel("start");
    if (event.target === this.refs.dialog) this.refs.dialog.close();
  };

  showPanel(name) {
    this.querySelectorAll("[data-popup-panel]").forEach(panel => {
      panel.hidden = panel.dataset.popupPanel !== name;
    });
  }

  handleSubmit = async event => {
    const form = event.target.closest("[data-popup-form]");
    if (!form) return;
    event.preventDefault();
    if (!form.checkValidity()) {
      form.querySelector("join-form-field[invalid] input")?.focus();
      return;
    }

    const status = form.querySelector("[data-form-status]");
    const submit = form.querySelector('[type="submit"]');

    const token = document.querySelector('meta[name="apim-auth-token"]')?.getAttribute("content");
    submit.disabled = true;
    status.dataset.state = "submitting";
    status.textContent = form.dataset.submittingLabel;

    try {
      const fields = new FormData(form);
      const { countryCode, languageCode } = getApiLocale();
      const headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(API_ENDPOINTS.leadManagement, {
        method: "POST",
        headers,
        body: JSON.stringify({
          siteType: "SHPFY",
          countryCode,
          langCode: languageCode,
          firstName: fields.get("first_name") || "",
          lastName: fields.get("last_name") || "",
          email: fields.get("email") || "",
          phone: fields.get("mobile") || "",
          postalCode: fields.get("zip_code") || "",
        }),
      });
      const result = await response.json().catch(() => null);

      if (response.status !== 201 || result?.meta_data?.status !== "created") {
        const apiMessage = result?.meta_data?.message?.filter(Boolean).join(" ");
        throw new Error(apiMessage || form.dataset.errorLabel);
      }

      // Reported before the redirect below: the "start an application" form
      // navigates away on success, and an event queued after assign() does not
      // survive the unload.
      trackFormSubmit(form);

      const redirect = form.dataset.redirect;
      if (redirect) {
        window.location.assign(withJoinParams(redirect));
        return;
      }
      status.dataset.state = "success";
      status.textContent = form.dataset.successLabel;
      form.reset();
    } catch (error) {
      status.dataset.state = "error";
      status.textContent = error.message || form.dataset.errorLabel;
      console.error("Popup form submission failed", error);
    } finally {
      submit.disabled = false;
    }
  };
}

if (!customElements.get("join-form-field")) customElements.define("join-form-field", JoinFormField);
if (!customElements.get("popup-modal")) customElements.define("popup-modal", PopupModal);
